/**
 * <LiveBrowserChoice> — comment rejoindre son live, hors de Chrome (organism).
 *
 * L'ancien `BrowserChoiceScreen`. Ce n'est pas un écran de panne, c'est une étape du parcours : le
 * but en titre, une raison en une ligne, une action réelle, la sortie en lien. Pas de triangle,
 * pas de procédure numérotée : deux versions s'y sont cassé les dents.
 *
 *  - `computer` : « Le live est plus stable sur Google Chrome. » → « Installer Google Chrome »
 *    (aucun navigateur ne peut en ouvrir un autre : pas de bouton « ouvrir Chrome »), et, pour
 *    qui l'a déjà, l'adresse à taper (`appDomain`, tirée de la configuration par l'écran) ;
 *  - `phone` : le navigateur d'un téléphone → « Ouvrir l'application Masteur ».
 * « Continuer sur ce navigateur » reste disponible, jamais proposé.
 *
 * Composes DS pieces only — `<Title>`, `<Text>`, `<Button>`, `<TextButton>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"

export type LiveBrowserChoiceProps = {
    /** `computer` : un autre navigateur que Chrome · `phone` : le navigateur d'un téléphone. */
    device: "computer" | "phone"
    /** L'adresse de l'app, sans son protocole — « app.masteur.com ». */
    appDomain: string
    /** « Installer Google Chrome » ou « Ouvrir l'application Masteur ». */
    onPrimary: () => void
    /** « Continuer sur ce navigateur ». */
    onContinueHere: () => void
    testID?: string
}

const TEXTS = {
    title: "Rejoindre ton live",
    reason: {
        computer: "Le live est plus stable sur Google Chrome.",
        phone: "Le live est plus stable dans l'application que dans le navigateur du téléphone.",
    },
    primary: { computer: "Installer Google Chrome", phone: "Ouvrir l'application Masteur" },
    alreadyComputer: "Tu l'as déjà ? Ouvre Chrome et va sur ",
    alreadyPhone: "Tu l'as déjà ? Ouvre-la, ton live t'y attend.",
    continueHere: "Continuer sur ce navigateur",
}

export function LiveBrowserChoice(props: LiveBrowserChoiceProps) {
    const { device, appDomain, onPrimary, onContinueHere, testID } = props

    return (
        <View style={styles.root} testID={testID}>
            <View style={styles.texts}>
                <Title center>{TEXTS.title}</Title>
                <Text size="text-lg" color="text-muted" center>
                    {TEXTS.reason[device]}
                </Text>
            </View>
            <Button label={TEXTS.primary[device]} onPress={onPrimary} fullWidth />
            <Text size="text-md" color="text-muted" center>
                {device === "phone" ? (
                    TEXTS.alreadyPhone
                ) : (
                    <>
                        {TEXTS.alreadyComputer}
                        <Text size="text-md" weight="bold" color="text-link">
                            {appDomain}
                        </Text>
                    </>
                )}
            </Text>
            <View style={styles.exit}>
                <TextButton label={TEXTS.continueHere} color="text-muted" onPress={onContinueHere} />
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: {
        gap: theme.spacing["4"],
    },
    texts: {
        gap: theme.spacing["3"],
        marginBottom: theme.spacing["3"],
    },
    exit: {
        alignItems: "center",
        marginTop: theme.spacing["5"],
    },
}))
